// Жизненный цикл проекта: открыть, автосохранение (1,2 с после изменения, ревизии с защитой от конфликта),
// сохранить сейчас, закрыть; живая проверка (lint) после изменений; запуск проверки по СТО.
import type { Editor } from '@tiptap/core';
import { api, ApiError } from './api';
import { appStore } from './store';
import { registry, type LintIssue } from './registry';
import { ctx } from './ctx';
import { currentIndex } from './docIndex';
import { refreshNumbering } from './editor/numberingPlugin';
import type { BuildResult, ReportFile } from './types';
import { cleanDoc, cleanMeta } from './schema/clean';
import { plainSpacesDeep } from './schema/constants';

const SAVE_DELAY = 1200;
let saveTimer: number | undefined;
let lintTimer: number | undefined;
let saving: Promise<void> | null = null;

export async function openProject(id: string) {
  // старые проекты и вставки прошлых версий: неразрывные пробелы -> обычные (USE_NBSP), полужирные метки в начале
  // абзацев и полужирный на листах «Задание»/«Реферат» - обычным (core/schema/clean.ts). Правка не сохраняется сама:
  // экспорт на сервере чистит так же, в report.json она попадёт с первым сохранением
  const raw: ReportFile = await api.loadProject(id);
  const report: ReportFile = { ...raw, doc: cleanDoc(raw.doc), meta: cleanMeta(raw.meta), sources: plainSpacesDeep(raw.sources || []) };
  appStore.getState().set({
    view: 'editor', projectId: id, revision: report.revision, meta: report.meta, sources: report.sources || [],
    initialDoc: report.doc, save: { state: 'saved', at: report.modified },
    // результат последней полной проверки (F7) хранится в проекте - виден и после повторного открытия
    check: { running: false, result: (report as any).lastCheck?.result || null, at: (report as any).lastCheck?.at ? (report as any).lastCheck.at * 1000 : undefined },
    lint: [],
    focusNode: null,
  });
  document.title = `${id} – Редактор СТО`;
  registry.events.emit('project:opened', { id, report });
}

export async function closeProject() {
  await saveNow();
  appStore.getState().set({ view: 'start', projectId: null, meta: null, sources: [], initialDoc: null, editor: null });
  document.title = 'Редактор СТО';
  registry.events.emit('project:closed');
}

/** Вызывается редактором (onUpdate) и при изменении метаданных/источников. */
export function scheduleSave() {
  window.clearTimeout(saveTimer);
  saveTimer = window.setTimeout(() => { void saveNow(); }, SAVE_DELAY);
}

/** always - сохранить, даже если нет изменений; overwrite - записать поверх чужой ревизии (конфликт). */
export async function saveNow(always = false, overwrite = false): Promise<void> {
  window.clearTimeout(saveTimer);
  if (saving) await saving;
  const s = appStore.getState();
  if (!s.projectId || !s.editor || s.editor.isDestroyed || s.readOnly) return;
  if (!always && !overwrite && s.save.state !== 'dirty') return;
  const pid = s.projectId;
  const payload = { doc: s.editor.getJSON(), meta: s.meta!, sources: s.sources, baseRevision: s.revision, force: overwrite };
  s.set({ save: { ...s.save, state: 'saving' } });
  saving = (async () => {
    try {
      const r = await api.saveProject(pid, payload);
      const cur = appStore.getState();
      if (cur.projectId !== pid) return;
      // если за время сохранения были новые правки - останемся «dirty»
      const stillDirty = cur.save.state === 'dirty';
      cur.set({ revision: r.revision, save: { state: stillDirty ? 'dirty' : 'saved', at: r.modified } });
      registry.events.emit('doc:saved', { id: pid, revision: r.revision });
      if (stillDirty) scheduleSave();
    } catch (e) {
      const err = e as ApiError;
      if (err.code === 'subscription_required') {
        // подписка закончилась за время работы: копия правок - в браузере (backup), дальше только просмотр
        registry.events.emit('doc:save-refused', { id: pid });
        appStore.getState().set({ save: { state: 'saved', at: appStore.getState().save.at } });
        return;
      }
      appStore.getState().set({ save: { state: err.status === 409 ? 'conflict' : 'error', error: err.message, code: err.code, info: err.data } });
      if (err.status === 409) {
        // сервер называет, кто изменил отчёт, только администратору (обычному пользователю - «в другой вкладке»)
        const other = /^Отчёт изменил/.test(err.message);
        ctx.toast(other ? `${err.message}. Ваши правки - копией в браузере: см. плашку над листом.`
          : 'Документ изменён в другой вкладке. Файл → «Сохранить поверх» или обновите страницу.', 'warn', 10000);
      } else if (err.code === 'project_deleted') {
        ctx.toast(err.message + '. Ваши правки - копией в браузере.', 'warn', 10000);
      }
    } finally {
      saving = null;
    }
  })();
  return saving;
}

// ------------------------------------------------------------------------------------------------ подписка редактора
export function attachEditor(editor: Editor) {
  appStore.getState().set({ editor });
  // React перед каждой фиксацией изменений интерфейса (строка состояния, лента…) запоминает выделение в фокусном
  // элементе: для contenteditable с contentEditable === 'true' он обходит DOM ВСЕГО документа до курсора
  // (getModernOffsetsFromPoints) - на отчёте в 100+ страниц это 5-10 мс на каждую фиксацию, несколько раз на
  // каждое нажатие. Выделение редактора React не восстанавливает (фокус не меняется) - работа впустую. Свойство
  // contentEditable корня для чтения возвращает 'inherit' (атрибут contenteditable, по которому работает браузер
  // и ProseMirror, не меняется).
  try {
    const dom = editor.view.dom as HTMLElement;
    Object.defineProperty(dom, 'contentEditable', {
      configurable: true,
      get() { return (this as HTMLElement).getAttribute('contenteditable') === 'false' ? 'false' : 'inherit'; },
      set(v: string) { (this as HTMLElement).setAttribute('contenteditable', String(v)); },
    });
  } catch { /* свойство не переопределяется - ничего страшного */ }
  editor.on('update', () => {
    appStore.getState().markDirty();
    scheduleSave();
    scheduleLint();
  });
  scheduleLint(50);
  registry.events.emit('editor:created', editor);
}

// метаданные и источники: сохранение + пересчёт номеров (раздел ИИ, порядок источников)
appStore.subscribe((s, prev) => {
  if (s.projectId && s.projectId === prev.projectId && (s.meta !== prev.meta || s.sources !== prev.sources) && prev.meta) {
    scheduleSave();
    refreshNumbering();
    scheduleLint();
    if (s.meta !== prev.meta) registry.events.emit('meta:changed', s.meta);
  }
});

// ------------------------------------------------------------------------------------------------ живая проверка
export function scheduleLint(delay = 600) {
  window.clearTimeout(lintTimer);
  lintTimer = window.setTimeout(runLint, delay);
}

export function runLint() {
  const ed = ctx.editor;
  if (!ed) return;
  const index = currentIndex(ed);
  const issues: LintIssue[] = [];
  for (const rule of registry.lint.list()) {
    try { issues.push(...rule.run(index, ctx)); } catch (e) { console.error('lint', rule.id, e); }
  }
  appStore.getState().set({ lint: issues });
}

// ------------------------------------------------------------------------------------------------ проверка по СТО
/** Сборка DOCX (+PDF в Word) и проверка sto_check. full=false - быстрая проверка без Word. */
export async function runCheck(full = true): Promise<BuildResult | null> {
  const s = appStore.getState();
  if (!s.projectId || s.check.running) return null;
  await saveNow();
  s.set({ check: { ...s.check, running: true, progress: 'Запуск…', error: null } });
  try {
    const { job } = await api.build(s.projectId, { pdf: full, check: true, word: full });
    const res = await api.waitJob<BuildResult>(job, (st) => appStore.getState().set({ check: { ...appStore.getState().check, progress: st.progress } }));
    appStore.getState().set({ check: { running: false, result: res, at: Date.now(), error: null } });
    registry.events.emit('check:done', res);
    return res;
  } catch (e: any) {
    appStore.getState().set({ check: { ...appStore.getState().check, running: false, error: e.message } });
    if (e?.code === 'cancelled') ctx.toast(e.message, e.data?.byAdmin ? 'warn' : 'info', 6000);
    else ctx.toast('Проверка не выполнена: ' + e.message, 'error', 8000);
    return null;
  }
}

window.addEventListener('beforeunload', (e) => {
  if (appStore.getState().save.state === 'dirty' || appStore.getState().save.state === 'saving') {
    void saveNow();
    e.preventDefault();
  }
});
